/**
 * PaymentOnboardingBanner — dismissible banner shown to admin users when the
 * Finix payment onboarding status changes (e.g. submitted, provisioning, approved).
 *
 * Dismissal is persisted server-side (on the clinic's ClinicFinixAccount) via
 * POST /api/finix/onboarding/dismiss-banner, so it survives logout and follows
 * the admin across devices. The banner reappears whenever the merchant status
 * advances to a new step.
 *
 * Rendered in Layout so it is visible on every page for admins.
 */
import React, { useState } from 'react';
import { Alert, Box, Typography, IconButton, Collapse } from '@mui/material';
import Payment from '@mui/icons-material/Payment';
import Close from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import {
  useDashboardBootstrap,
  DASHBOARD_BOOTSTRAP_QUERY_KEY,
} from '../../../hooks/useDashboardBootstrap';
import { useApiMutation } from '../../../hooks/useApiQuery';

interface OnboardingStatus {
  merchant_status: string;
  onboarding_link_url?: string;
  banner_dismissed_status?: string | null;
}

/** Human-readable labels and severity for each merchant status. */
const STATUS_INFO: Record<string, { message: string; severity: 'info' | 'warning' | 'success' | 'error' }> = {
  in_progress: {
    message: 'Payment onboarding is in progress. Complete the Finix form to enable payments.',
    severity: 'info',
  },
  submitted: {
    message: 'Payment onboarding has been submitted and is under review.',
    severity: 'info',
  },
  pending: {
    message: 'Payment onboarding is pending review.',
    severity: 'info',
  },
  provisioning: {
    message: 'Your payment account is being provisioned. This typically takes 1-2 business days.',
    severity: 'info',
  },
  approved: {
    message: 'Payment processing has been approved! Your practice can now accept payments.',
    severity: 'success',
  },
  rejected: {
    message: 'Payment onboarding was not approved. Please review and resubmit.',
    severity: 'error',
  },
  update_requested: {
    message: 'Additional information is needed for your payment onboarding application.',
    severity: 'warning',
  },
};

interface PaymentOnboardingBannerProps {
  hasAdminSettingsAccess?: boolean;
}

export default function PaymentOnboardingBanner({
  hasAdminSettingsAccess,
}: PaymentOnboardingBannerProps) {
  // Optimistically hide immediately on click; the server persists the
  // dismissal so it survives logout and refetches.
  const [dismissed, setDismissed] = useState(false);

  // Finix onboarding status comes from the shared dashboard bootstrap
  // bundle.  The backend returns `null` for users without admin settings
  // access, so we do not need a separate permission check here.
  const { data: bootstrap } = useDashboardBootstrap();
  const data = (hasAdminSettingsAccess
    ? (bootstrap?.finix_onboarding ?? null)
    : null) as OnboardingStatus | null;

  const merchantStatus = data?.merchant_status || null;
  const dismissedStatus = data?.banner_dismissed_status ?? null;

  const { mutate: persistDismiss } = useApiMutation(
    '/api/finix/onboarding/dismiss-banner',
    'post',
    [DASHBOARD_BOOTSTRAP_QUERY_KEY],
  );

  const handleDismiss = () => {
    setDismissed(true);
    persistDismiss({});
  };

  // Don't render if not admin, still loading, dismissed, or status is not_started
  if (!hasAdminSettingsAccess || !merchantStatus || dismissed) return null;
  if (merchantStatus === 'not_started') return null;

  // Hide if this status was already dismissed (persisted server-side)
  if (dismissedStatus === merchantStatus) return null;

  const info = STATUS_INFO[merchantStatus];
  if (!info) return null;

  return (
    <Collapse in={true}>
      <Alert
        severity={info.severity}
        icon={<Payment />}
        sx={{
          borderRadius: 0,
          py: 0.5,
          px: 1,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        action={
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss payment status notification"
            sx={{ color: 'inherit' }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {info.message}
          </Typography>
          <Link
            to="/admin/settings?tab=payments"
            onClick={handleDismiss}
            style={{ textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 8 }}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              View Payments &rarr;
            </Typography>
          </Link>
        </Box>
      </Alert>
    </Collapse>
  );
}
